import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowUpRightIcon, LockIcon } from 'lucide-react';
import { useCaseStudyAccess } from '../../contexts/CaseStudyAccessContext';
import { Reveal } from '../Reveal';
import { WorkVisual } from './work-visuals/WorkVisual';
import type { WorkItem } from '../../types/work';
import { EASE_OUT } from '../../utils/motion';
type WorkCardProps = {
  item: WorkItem;
};
const ARROW_TRANSITION = {
  duration: 0.25,
  ease: EASE_OUT
};
// No spring here: overshoot made the button wobble when the pointer moved in and out quickly.
const BUTTON_TWEEN = {
  duration: 0.22,
  ease: EASE_OUT
};
export function WorkCard({
  item
}: WorkCardProps) {
  const {
    isUnlocked,
    requestAccess
  } = useCaseStudyAccess();
  const isLink = Boolean(item.href);
  const isLocked = Boolean(item.locked) && !isUnlocked;
  const [name, ...rest] = item.title.split(':');
  const claim = rest.join(':').trim();
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (!isLocked || !item.href) return;
    e.preventDefault();
    requestAccess(item.href);
  };
  const content = <div className="rounded-3xl border border-line bg-raised p-3 transition-[border-color] duration-200 ease-out group-hover:border-fg/20 sm:p-4" data-id="element-383" data-mp="wnnummq55oz">
      {/* The mask keeps the rounded clip stable (Safari) without forcing an extra GPU layer per card,
          which re-rasterised and flashed while the card faded in on scroll. */}
      <div className="relative isolate aspect-[16/10] overflow-hidden rounded-2xl [-webkit-mask-image:-webkit-radial-gradient(white,black)] sm:aspect-[16/9]" data-id="element-384" data-mp="jpi3lis691u9">
        <div className="absolute inset-0" data-id="element-385" data-mp="xo812ziwl3j5">
          <WorkVisual item={item} data-id="element-386" data-mp="spen75zpfaig" />
        </div>
        {isLink && <motion.span aria-hidden className="absolute bottom-4 right-4 grid h-14 w-14 origin-bottom-right place-items-center overflow-hidden rounded-full text-fg shadow-[0_8px_24px_rgba(0,0,0,0.18)] [backface-visibility:hidden] [will-change:transform] sm:bottom-6 sm:right-6 sm:h-20 sm:w-20" style={{
        backgroundColor: '#FFFFFF'
      }} variants={{
        rest: {
          scale: 1,
          backgroundColor: '#FFFFFF'
        },
        hover: {
          scale: 1.15,
          backgroundColor: '#1E4B48'
        },
        press: {
          scale: 1.08,
          backgroundColor: '#1E4B48'
        }
      }} transition={BUTTON_TWEEN} data-id="element-387" data-mp="so3k78e326se">
            <span className="relative grid h-6 w-6 place-items-center overflow-hidden" data-id="element-388" data-mp="1hbzcya0rss4a">
              <motion.span className="absolute" variants={{
            rest: {
              x: 0,
              y: 0
            },
            hover: {
              x: '140%',
              y: '-140%'
            },
            press: {
              x: '140%',
              y: '-140%'
            }
          }} transition={ARROW_TRANSITION} data-id="element-389" data-mp="1mbbviiklzd12">
                <ArrowUpRightIcon className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={1.75} data-id="element-390" data-mp="1w3zqd7s136x5" />
              </motion.span>
              <motion.span className="absolute text-white" variants={{
            rest: {
              x: '-140%',
              y: '140%'
            },
            hover: {
              x: 0,
              y: 0
            },
            press: {
              x: 0,
              y: 0
            }
          }} transition={{
            ...ARROW_TRANSITION,
            delay: 0.04
          }} data-id="element-391" data-mp="252l66v6487u8">
                <ArrowUpRightIcon className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={1.75} data-id="element-392" data-mp="3kjzpqmeakxwb" />
              </motion.span>
            </span>
          </motion.span>}
      </div>

      <div className="px-2 pb-3 pt-6 sm:px-3 sm:pb-4" data-id="element-393" data-mp="3blw9o5m2stbw">
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2" data-id="element-394" data-mp="c5pwwj7y4zr2">
          <p className="text-[13px] font-semibold text-ink" data-id="element-395" data-mp="c7ka1bcz7nkl">{item.label ?? name}</p>
          <div className="flex items-center gap-3" data-id="element-396" data-mp="84xlpkfikks5">
            {isLink && isLocked && <span className="inline-flex items-center gap-1 rounded-full bg-coral-soft px-2 py-0.5 text-[11px] font-medium text-rust" data-id="element-397" data-mp="3bl1dmhvb057">
                <LockIcon className="h-3 w-3" aria-hidden data-id="element-398" data-mp="vtgkn9cobl7k" />
                Password
              </span>}
            <p className="text-xs text-fg-faint" data-id="element-399" data-mp="r2gs0gal7pav">{item.role}</p>
          </div>
        </div>
        <h3 className={`mt-2 text-xl font-semibold leading-snug tracking-tight text-fg first-letter:uppercase md:text-2xl ${isLink ? 'transition-colors duration-150 group-hover:text-ink' : ''}`} data-id="element-400" data-mp="amzhjq2hf9wa">
          {claim || name}
        </h3>
        <p className="mt-2.5 max-w-2xl text-sm leading-relaxed text-fg-muted" data-id="element-401" data-mp="6lw0x1cm19u0">{item.summary}</p>
        <div className="mt-5 flex flex-wrap items-center justify-between gap-3" data-id="element-402" data-mp="6nqkocc5avwi">
          <ul className="flex flex-wrap gap-1.5" aria-label="Tags" data-id="element-403" data-mp="udkzv0jxswjx">
            {item.tags.map(tag => <li key={tag} className="rounded-full bg-surface px-2.5 py-1 text-[11px] font-medium text-fg-muted" data-id="element-404" data-mp="yewgigku27fz">
                {tag}
              </li>)}
          </ul>
          {!isLink && <span className="text-xs text-fg-faint" data-id="element-405" data-mp="pinjt9o8pmxj">Case study coming soon</span>}
        </div>
      </div>
    </div>;
  if (item.href) {
    return <Reveal data-id="element-406" data-mp="1i246zcr933af">
        <motion.div initial="rest" animate="rest" whileHover="hover" whileTap="press" data-id="element-407" data-mp="1i1udhf6zydzz">
          <Link to={item.href} onClick={handleClick} aria-haspopup={isLocked ? 'dialog' : undefined} aria-label={`Read case study: ${item.title}${isLocked ? ' (password protected)' : ''}`} className="group block rounded-3xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/30 focus-visible:ring-offset-4 focus-visible:ring-offset-canvas" data-id="element-408" data-mp="1ret9xe0umlu3">
            {content}
          </Link>
        </motion.div>
      </Reveal>;
  }
  return <Reveal data-id="element-409" data-mp="14h81k7w4olvk">
      <article data-id="element-410" data-mp="14hrmlxh2rqdt">{content}</article>
    </Reveal>;
}